// src/components/familyoffice/SalePreparation.jsx
import React, { useState, useEffect } from "react";
import { supabase } from "@/lib/supabaseClient";
import {
  FileText,
  AlertCircle,
  CheckCircle,
  Download,
  Mail,
  X,
} from "lucide-react";

// 🔧 Paramétrage des exigences par pays de vente
const COUNTRY_RULESETS = {
  FR: {
    label: "France",
    legallyRequired: [
      "title",
      "artist",
      "date",
      "medium",
      "dimensions",
      "provenance",
      "condition",
      "valuation",
    ],
    ownerAttestations: {
      authenticity: true, // coche obligatoire si pas de certificat
      property: true, // coche obligatoire
    },
    recommended: [
      "images",
      "condition_report",
      "purchase_location",
      "certification",
    ],
  },
  DEFAULT: {
    label: "Standard international",
    legallyRequired: [
      "title",
      "artist",
      "date",
      "medium",
      "dimensions",
      "provenance",
      "condition",
    ],
    ownerAttestations: {
      authenticity: true,
      property: true,
    },
    recommended: ["images", "valuation"],
  },
};

export default function SalePreparation({ artwork, onClose }) {
  const [loading, setLoading] = useState(true);
  const [completeness, setCompleteness] = useState({});
  const [showNDA, setShowNDA] = useState(false);
  const [ndaText, setNdaText] = useState("");
  const [experts, setExperts] = useState([]);

  // Pays de vente (FR par défaut pour l’instant)
  const [saleCountry, setSaleCountry] = useState("FR");

  // Contact = expert ou avocat
  const [contactMode, setContactMode] = useState("expert"); // "expert" | "lawyer"
  const [selectedExpertId, setSelectedExpertId] = useState("");

  const [lawyerInfo, setLawyerInfo] = useState({
    name: "",
    firm: "",
    email: "",
    phone: "",
  });

  // Attestations FO / propriétaire
  const [userCheckedAuthenticity, setUserCheckedAuthenticity] =
    useState(false);
  const [userCheckedProperty, setUserCheckedProperty] = useState(false);

  // Charger complétude + experts au montage / changement d’œuvre
  useEffect(() => {
    setLoading(true);
    setUserCheckedAuthenticity(false);
    setUserCheckedProperty(false);
    checkCompleteness();
    loadExperts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artwork]);

  // Régénérer le NDA quand les infos clés changent
  useEffect(() => {
    if (!loading) {
      generateDefaultNDA();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    contactMode,
    lawyerInfo,
    selectedExpertId,
    loading,
    userCheckedAuthenticity,
    userCheckedProperty,
    saleCountry,
  ]);

  // -------- Vérifier la complétude des données --------
  const checkCompleteness = () => {
    // Propreté provenance
    let provenanceDisplay = null;
    if (artwork.provenance) {
      let provenanceData = artwork.provenance;

      if (typeof provenanceData === "string") {
        try {
          const trimmed = provenanceData.trim();
          if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
            provenanceData = JSON.parse(trimmed);
          } else {
            provenanceDisplay = provenanceData;
          }
        } catch (e) {
          provenanceDisplay = provenanceData;
        }
      }

      if (!provenanceDisplay && provenanceData) {
        if (Array.isArray(provenanceData)) {
          provenanceDisplay = provenanceData
            .map((p) => {
              const date = p.date || "";
              const desc = p.description || p.location || "";
              return `${date} - ${desc}`
                .trim()
                .replace(/^- /, "")
                .replace(/ -$/, "");
            })
            .filter(Boolean)
            .join(" • ");
        } else if (typeof provenanceData === "object") {
          const date = provenanceData.date || "";
          const desc =
            provenanceData.description || provenanceData.location || "";
          provenanceDisplay = `${date} - ${desc}`
            .trim()
            .replace(/^- /, "")
            .replace(/ -$/, "");
        }
      }
    }

    const required = {
      images: {
        label: "Visuels HD",
        status: artwork.images?.length > 0 || artwork.image_url,
        data:
          artwork.images ||
          (artwork.image_url ? [artwork.image_url] : []),
      },
      title: {
        label: "Titre",
        status: !!artwork.title,
        data: artwork.title,
      },
      artist: {
        label: "Artiste",
        status: !!artwork.artist,
        data: artwork.artist,
      },
      date: {
        label: "Date/Année",
        status: !!artwork.year,
        data: artwork.year,
      },
      medium: {
        label: "Medium",
        status: !!artwork.medium,
        data: artwork.medium,
      },
      signature: {
        label: "Signature",
        status: !!artwork.signature,
        data: artwork.signature,
      },
      certification: {
        label: "Certificat d'authenticité",
        status: !!artwork.certificate_authenticity,
        data: artwork.certificate_authenticity,
      },
      dimensions: {
        label: "Dimensions",
        status: !!(
          artwork.specifications?.dimensions?.height ||
          artwork.specifications?.dimensions?.width
        ),
        data:
          artwork.specifications?.dimensions?.height &&
          artwork.specifications?.dimensions?.width
            ? `${artwork.specifications.dimensions.height} x ${
                artwork.specifications.dimensions.width
              }${
                artwork.specifications.dimensions.depth
                  ? ` x ${artwork.specifications.dimensions.depth}`
                  : ""
              } ${artwork.specifications.dimensions.unit || "cm"}`
            : null,
      },

      condition: {
        label: "État de conservation",
        status: !!artwork.condition,
        data: artwork.condition,
      },
      condition_report: {
        label: "Condition report (document)",
        status: !!(
          artwork.condition_report ||
          artwork.condition_report_url ||
          (Array.isArray(artwork.documents) &&
            artwork.documents.some(
              (d) => d && d.type === "condition_report"
            ))
        ),
        data:
          artwork.condition_report ||
          artwork.condition_report_url ||
          "Document disponible",
      },
      provenance: {
        label: "Provenance",
        status: !!provenanceDisplay,
        data: provenanceDisplay,
      },
      purchase_location: {
        label: "Lieu d'achat (ville/pays)",
        status: !!(artwork.purchase_city || artwork.purchase_country),
        data: [artwork.purchase_city, artwork.purchase_country]
          .filter(Boolean)
          .join(", "),
      },
      valuation: {
        label: "Estimation de valeur",
        status: !!(
          artwork.current_estimated_value || artwork.valuation_estimated_value || artwork.estimated_value
        ),
        data: artwork.current_estimated_value || artwork.valuation_estimated_value || artwork.estimated_value,
      },
    };

    setCompleteness(required);
    setLoading(false);
  };

  // -------- Charger les experts disponibles --------
  const loadExperts = async () => {
    const { data } = await supabase
      .from("profiles")
      .select("id, first_name, last_name, company, email")
      .eq("role", "expert")
      .eq("approved", true)
      .limit(50);

    setExperts(data || []);
  };

  const selectedExpert =
    experts.find((e) => e.id === selectedExpertId) || null;

  // -------- Générer le NDA par défaut (texte éditable) --------
  const generateDefaultNDA = () => {
    const today = new Date().toLocaleDateString("fr-FR");
    const rules = COUNTRY_RULESETS[saleCountry] || COUNTRY_RULESETS.DEFAULT;

    let representative = "Représenté par [NOM DU FAMILY OFFICE]";
    let contactInfo = "";

    if (contactMode === "lawyer" && lawyerInfo.name) {
      representative = `Représenté par Maître ${lawyerInfo.name}${
        lawyerInfo.firm ? ` - ${lawyerInfo.firm}` : ""
      }`;
      if (lawyerInfo.email) {
        contactInfo = `\nContact : ${lawyerInfo.email}${
          lawyerInfo.phone ? ` - ${lawyerInfo.phone}` : ""
        }`;
      }
    } else if (contactMode === "expert" && selectedExpert) {
      const fullName = `${selectedExpert.first_name || ""} ${
        selectedExpert.last_name || ""
      }`.trim();
      representative = `Représenté par ${fullName || "l'Expert désigné"}${
        selectedExpert.company ? ` - ${selectedExpert.company}` : ""
      }`;
      if (selectedExpert.email) {
        contactInfo = `\nContact : ${selectedExpert.email}`;
      }
    }

    const authenticityClause = rules.ownerAttestations.authenticity
      ? `Le Vendeur garantit l’authenticité de l’œuvre. En l’absence de certificat d’authenticité, cette garantie est donnée sous sa seule responsabilité, conformément à son obligation de délivrance (article 1603 du Code civil).`
      : "";

    const propertyClause = rules.ownerAttestations.property
      ? `Le Vendeur atteste être le propriétaire légitime de l’œuvre et garantit qu’elle n’est pas issue d’un vol, d’un trafic illicite ou d’un transfert illégal, conformément à l’article 2279 du Code civil et, le cas échéant, à la Convention de l’UNESCO de 1970.`
      : "";

    const nda = `ACCORD DE CONFIDENTIALITÉ

Entre les soussignés :

D'une part,
Le Propriétaire de l'œuvre d'art décrite ci-après
${representative}${contactInfo}

Et d'autre part,
Le Destinataire des informations confidentielles

Il a été convenu ce qui suit :

ARTICLE 1 - OBJET
Le présent accord a pour objet de définir les conditions dans lesquelles le Propriétaire communique au Destinataire des informations confidentielles concernant l'œuvre suivante :

Œuvre : ${artwork.title || "[Titre]"}
Artiste : ${artwork.artist || "[Artiste]"}
Date : ${artwork.year || "[Date]"}

ARTICLE 2 - DÉFINITION DES INFORMATIONS CONFIDENTIELLES
Sont considérées comme confidentielles toutes les informations relatives à :
- La provenance de l'œuvre
- Son état de conservation détaillé
- Son estimation de valeur
- L'identité du propriétaire actuel
- Les documents d'authentification
- Les coordonnées et localisation précise

ARTICLE 3 - GARANTIES DU VENDEUR
${authenticityClause || "[Garantie d’authenticité à préciser]"}
${propertyClause || "[Attestation de propriété à préciser]"}

ARTICLE 4 - OBLIGATIONS DU DESTINATAIRE
Le Destinataire s'engage à :
- Traiter les informations avec la plus stricte confidentialité
- Ne les utiliser que dans le cadre d'une éventuelle transaction
- Ne pas les divulguer à des tiers sans autorisation écrite préalable
- Restituer ou détruire tous documents confidentiels sur demande

ARTICLE 5 - DURÉE
Le présent accord prend effet à compter de sa signature et demeure valable pour une durée de 2 ans.

ARTICLE 6 - LOI APPLICABLE
Le présent accord est régi par le droit de ${
      rules.label || "la juridiction compétente"
    }.

Fait à [VILLE], le ${today}

Le Propriétaire                           Le Destinataire
[Signature]                               [Signature]`;

    setNdaText(nda);
  };

  // -------- Helper prix pour le PDF --------
  const formatPriceForPDF = (value) => {
    const numValue =
      typeof value === "string"
        ? parseFloat(value.replace(/[^\d.-]/g, ""))
        : Number(value);

    if (isNaN(numValue)) return String(value || "");

    const parts = numValue.toFixed(0).toString().split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return parts.join(",") + " EUR";
  };

  // -------- Générer le ZIP (NDA + PDF + images) --------
  const generateSaleDossier = async () => {
    const rules = COUNTRY_RULESETS[saleCountry] || COUNTRY_RULESETS.DEFAULT;

    const legalRequiredKeys = rules.legallyRequired || [];
    const isLegallyComplete =
      legalRequiredKeys.length === 0 ||
      legalRequiredKeys.every((key) => completeness[key]?.status);

    const isLawyerValid =
      contactMode === "lawyer" && lawyerInfo.name && lawyerInfo.email;
    const isExpertValid =
      contactMode === "expert" && !!selectedExpertId;
    const isContactOk = isLawyerValid || isExpertValid;

    const needsAuthAttestation =
      rules.ownerAttestations?.authenticity &&
      !completeness.certification?.status;
    const isAuthenticityOk =
      !needsAuthAttestation || userCheckedAuthenticity;

    const needsPropertyAttestation =
      rules.ownerAttestations?.property ?? false;
    const isPropertyOk =
      !needsPropertyAttestation || userCheckedProperty;

    const isSaleAuthorized =
      isLegallyComplete && isContactOk && isAuthenticityOk && isPropertyOk;

    if (!isSaleAuthorized) {
      const missingLegal = (rules.legallyRequired || []).filter(
        (key) => !completeness[key]?.status
      );
      let msg = `Le dossier ne peut pas être généré.\n\nPour une vente en ${rules.label}, il faut :\n`;
      if (missingLegal.length > 0) {
        msg += `\n• Compléter les champs obligatoires : ${missingLegal
          .map((k) => completeness[k]?.label)
          .filter(Boolean)
          .join(", ")}.`;
      }
      if (!isContactOk) {
        msg += `\n• Désigner un EXPERT ou un AVOCAT (nom + email).`;
      }
      if (!isAuthenticityOk) {
        msg += `\n• Cocher la garantie d’authenticité.`;
      }
      if (!isPropertyOk) {
        msg += `\n• Cocher l’attestation de propriété légitime.`;
      }
      alert(msg);
      return;
    }

    try {
      const JSZip = (await import("jszip")).default;
      const {
        Document,
        Paragraph,
        TextRun,
        AlignmentType,
        HeadingLevel,
        Packer,
      } = await import("docx");
      const { jsPDF } = await import("jspdf");

      const zip = new JSZip();

      // 1️⃣ NDA Word
      const ruleset = rules;

      const ndaDoc = new Document({
        sections: [
          {
            properties: {},
            children: [
              new Paragraph({
                text: "ACCORD DE CONFIDENTIALITÉ",
                heading: HeadingLevel.HEADING_1,
                alignment: AlignmentType.CENTER,
              }),
              new Paragraph({ text: "" }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: "Entre les soussignés :",
                    bold: true,
                  }),
                ],
              }),
              new Paragraph({ text: "" }),
              new Paragraph({ text: "D'une part," }),
              new Paragraph({
                text: "Le Propriétaire de l'œuvre d'art décrite ci-après",
              }),
              new Paragraph({
                text:
                  contactMode === "lawyer" && lawyerInfo.name
                    ? `Représenté par Maître ${lawyerInfo.name}${
                        lawyerInfo.firm ? ` - ${lawyerInfo.firm}` : ""
                      }`
                    : contactMode === "expert" && selectedExpert
                    ? `Représenté par ${
                        `${selectedExpert.first_name || ""} ${
                          selectedExpert.last_name || ""
                        }`.trim() || "l'Expert désigné"
                      }${
                        selectedExpert.company
                          ? ` - ${selectedExpert.company}`
                          : ""
                      }`
                    : "Représenté par [NOM DU FAMILY OFFICE]",
              }),
              new Paragraph({ text: "" }),
              new Paragraph({ text: "Et d'autre part," }),
              new Paragraph({
                text: "Le Destinataire des informations confidentielles",
              }),
              new Paragraph({ text: "" }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: "ARTICLE 1 - OBJET",
                    bold: true,
                  }),
                ],
              }),
              new Paragraph({
                text: `Le présent accord a pour objet de définir les conditions dans lesquelles le Propriétaire communique au Destinataire des informations confidentielles concernant l'œuvre suivante :`,
              }),
              new Paragraph({ text: "" }),
              new Paragraph({
                text: `Œuvre : ${artwork.title || "[Titre]"}`,
              }),
              new Paragraph({
                text: `Artiste : ${artwork.artist || "[Artiste]"}`,
              }),
              new Paragraph({
                text: `Date : ${artwork.year || "[Date]"}`,
              }),
              new Paragraph({ text: "" }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: "ARTICLE 2 - INFORMATIONS CONFIDENTIELLES",
                    bold: true,
                  }),
                ],
              }),
              new Paragraph({
                text: "Sont considérées comme confidentielles toutes les informations relatives notamment à :",
              }),
              new Paragraph({ text: "- La provenance de l'œuvre" }),
              new Paragraph({
                text: "- Son état de conservation détaillé",
              }),
              new Paragraph({ text: "- Son estimation de valeur" }),
              new Paragraph({
                text: "- L'identité du propriétaire actuel",
              }),
              new Paragraph({
                text: "- Les documents d'authentification",
              }),
              new Paragraph({
                text: "- Les coordonnées et localisation précise",
              }),
              new Paragraph({ text: "" }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: "ARTICLE 3 - GARANTIES DU VENDEUR",
                    bold: true,
                  }),
                ],
              }),
              ruleset.ownerAttestations?.authenticity
                ? new Paragraph({
                    text: "Le Vendeur garantit l’authenticité de l’œuvre. En l’absence de certificat d’authenticité, cette garantie est donnée sous sa seule responsabilité, conformément à son obligation de délivrance (article 1603 du Code civil).",
                  })
                : new Paragraph({
                    text: "[Garantie d’authenticité à préciser]",
                  }),
              ruleset.ownerAttestations?.property
                ? new Paragraph({
                    text: "Le Vendeur atteste être le propriétaire légitime de l’œuvre et garantit qu’elle n’est pas issue d’un vol, d’un trafic illicite ou d’un transfert illégal, conformément à l’article 2279 du Code civil et, le cas échéant, à la Convention de l’UNESCO de 1970.",
                  })
                : new Paragraph({
                    text: "[Attestation de propriété à préciser]",
                  }),
              new Paragraph({ text: "" }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: "ARTICLE 4 - OBLIGATIONS DU DESTINATAIRE",
                    bold: true,
                  }),
                ],
              }),
              new Paragraph({
                text: "Le Destinataire s'engage à traiter les informations avec la plus stricte confidentialité, à ne les utiliser que dans le cadre d'une éventuelle transaction, à ne pas les divulguer à des tiers sans autorisation écrite préalable, et à restituer ou détruire tous documents confidentiels sur demande.",
              }),
              new Paragraph({ text: "" }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: "ARTICLE 5 - DURÉE",
                    bold: true,
                  }),
                ],
              }),
              new Paragraph({
                text: "Le présent accord prend effet à compter de sa signature et demeure valable pour une durée de 2 ans.",
              }),
              new Paragraph({ text: "" }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: "ARTICLE 6 - LOI APPLICABLE",
                    bold: true,
                  }),
                ],
              }),
              new Paragraph({
                text: `Le présent accord est régi par le droit de ${
                  ruleset.label || "la juridiction compétente"
                }.`,
              }),
              new Paragraph({ text: "" }),
              new Paragraph({
                text: `Fait à [VILLE], le ${new Date().toLocaleDateString(
                  "fr-FR"
                )}`,
              }),
              new Paragraph({ text: "" }),
              new Paragraph({
                text: "Le Propriétaire                           Le Destinataire",
              }),
              new Paragraph({
                text: "[Signature]                               [Signature]",
              }),
            ],
          },
        ],
      });

      const ndaBlob = await Packer.toBlob(ndaDoc);
      zip.file("NDA.docx", ndaBlob);

      // 2️⃣ PDF dossier
      const pdf = new jsPDF();
      let yPos = 20;

      pdf.setFontSize(20);
      pdf.text("Dossier de mise en vente", 20, yPos);
      yPos += 15;

      pdf.setFontSize(12);
      pdf.text(`${artwork.title || "Sans titre"}`, 20, yPos);
      yPos += 7;
      pdf.setFontSize(10);
      pdf.text(
        `${artwork.artist || "Artiste inconnu"} - ${
          artwork.year || "Date inconnue"
        }`,
        20,
        yPos
      );
      yPos += 15;

      pdf.setFontSize(11);
      Object.entries(completeness).forEach(([key, info]) => {
        if (info.status && key !== "images") {
          const label = info.label;
          let value = "";

          if (key === "valuation") {
            value = formatPriceForPDF(info.data);
          } else {
            value = String(info.data || "");
          }

          pdf.setFont(undefined, "bold");
          pdf.text(`${label}:`, 20, yPos);
          pdf.setFont(undefined, "normal");

          const lines = pdf.splitTextToSize(value, 170);
          pdf.text(lines, 20, yPos + 5);
          yPos += 5 + lines.length * 5 + 3;

          if (yPos > 270) {
            pdf.addPage();
            yPos = 20;
          }
        }
      });

      // Images note
      yPos += 5;
      if (!artwork.images?.length && !artwork.image_url) {
        if (yPos > 260) {
          pdf.addPage();
          yPos = 20;
        }
        pdf.setFont(undefined, "italic");
        pdf.setFontSize(9);
        pdf.text(
          "Visuels disponibles sur demande après signature du NDA",
          20,
          yPos
        );
        pdf.setFontSize(10);
        pdf.setFont(undefined, "normal");
        yPos += 8;
      }

      // Contact
      yPos += 10;
      if (yPos > 250) {
        pdf.addPage();
        yPos = 20;
      }
      pdf.setFont(undefined, "bold");
      pdf.text("Contact pour la vente:", 20, yPos);
      yPos += 7;
      pdf.setFont(undefined, "normal");

      if (contactMode === "lawyer" && lawyerInfo.name) {
        pdf.text(`Maître ${lawyerInfo.name}`, 20, yPos);
        yPos += 5;
        if (lawyerInfo.firm) {
          pdf.text(lawyerInfo.firm, 20, yPos);
          yPos += 5;
        }
        if (lawyerInfo.email) {
          pdf.text(lawyerInfo.email, 20, yPos);
          yPos += 5;
        }
        if (lawyerInfo.phone) {
          pdf.text(lawyerInfo.phone, 20, yPos);
        }
      } else if (contactMode === "expert" && selectedExpert) {
        const fullName = `${selectedExpert.first_name || ""} ${
          selectedExpert.last_name || ""
        }`.trim();
        pdf.text(fullName || "Expert désigné", 20, yPos);
        yPos += 5;
        if (selectedExpert.company) {
          pdf.text(selectedExpert.company, 20, yPos);
          yPos += 5;
        }
        if (selectedExpert.email) {
          pdf.text(selectedExpert.email, 20, yPos);
        }
      }

      const pdfBlob = pdf.output("blob");
      zip.file("Dossier_Vente.pdf", pdfBlob);

      // 3️⃣ Images
      if (artwork.images && artwork.images.length > 0) {
        const imagesFolder = zip.folder("images");
        for (let i = 0; i < artwork.images.length; i++) {
          const imageUrl = artwork.images[i];
          try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const ext =
              imageUrl.split(".").pop().split("?")[0] || "jpg";
            imagesFolder.file(`image_${i + 1}.${ext}`, blob);
          } catch (e) {
            console.warn(
              `Impossible de télécharger l'image ${i + 1}`,
              e
            );
          }
        }
      } else if (artwork.image_url) {
        try {
          const response = await fetch(artwork.image_url);
          const blob = await response.blob();
          const ext =
            artwork.image_url.split(".").pop().split("?")[0] ||
            "jpg";
          zip.file(`images/image_1.${ext}`, blob);
        } catch (e) {
          console.warn("Impossible de télécharger l'image", e);
        }
      }

      // 4️⃣ Téléchargement ZIP
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Dossier_Vente_${(artwork.artist || "")
        .replace(/\s/g, "_")
        .slice(0, 50)}_${(artwork.title || "")
        .replace(/\s/g, "_")
        .slice(0, 50)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      alert("Dossier téléchargé avec succès !");
    } catch (error) {
      console.error("Erreur lors de la génération:", error);
      alert(
        "Erreur lors de la génération du dossier. Vérifiez que toutes les librairies sont installées."
      );
    }
  };

  // -------- Contacter un expert par mail --------
  const contactExpert = () => {
    if (!selectedExpert) return;
    const subject = `Complément d'information requis - ${artwork.title}`;
    const body = `Bonjour,

Je prépare un dossier de mise en vente pour l'œuvre suivante et j'ai besoin de votre expertise pour compléter certaines informations :

Œuvre : ${artwork.title || "Sans titre"}
Artiste : ${artwork.artist || "Inconnu"}

Informations manquantes :
${Object.entries(completeness)
  .filter(([_, info]) => !info.status)
  .map(([_, info]) => `- ${info.label}`)
  .join("\n")}

Pourriez-vous m'aider à compléter ces informations ?

Cordialement`;

    window.location.href = `mailto:${
      selectedExpert.email
    }?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
      body
    )}`;
  };

  const missingCount = Object.values(completeness).filter(
    (c) => !c.status
  ).length;
  const totalCount = Object.keys(completeness).length;
  const isComplete = totalCount > 0 && missingCount === 0;

  const rules = COUNTRY_RULESETS[saleCountry] || COUNTRY_RULESETS.DEFAULT;
  const legalRequiredKeys = rules.legallyRequired || [];
  const isLegallyComplete =
    legalRequiredKeys.length === 0 ||
    legalRequiredKeys.every((key) => completeness[key]?.status);

  const needsAuthAttestation =
    rules.ownerAttestations?.authenticity &&
    !completeness.certification?.status;
  const needsPropertyAttestation =
    rules.ownerAttestations?.property ?? false;

  const isLawyerValid =
    contactMode === "lawyer" && lawyerInfo.name && lawyerInfo.email;
  const isExpertValid =
    contactMode === "expert" && !!selectedExpertId;
  const isContactOk = isLawyerValid || isExpertValid;

  const isAuthenticityOk =
    !needsAuthAttestation || userCheckedAuthenticity;
  const isPropertyOk =
    !needsPropertyAttestation || userCheckedProperty;

  const isSaleAuthorized =
    isLegallyComplete && isContactOk && isAuthenticityOk && isPropertyOk;

  if (loading && !totalCount) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-2xl px-6 py-4 text-sm">
          Analyse de l'œuvre en cours…
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-4xl rounded-xl shadow-2xl max-h-[90vh] flex flex-col relative">
        {/* Header */}
        <div className="bg-white border-b border-[#E8E8E8] px-6 py-3 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-serif text-[#1A1A1A]">
              Dossier de mise en vente
            </h2>
            <p className="text-xs text-[#666]">
              {artwork.title || "Sans titre"} •{" "}
              {artwork.artist || "Artiste inconnu"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-[#666] hover:text-[#1A1A1A]"
          >
            <X size={18} />
          </button>
        </div>

        {/* Contenu */}
        <div className="px-6 py-4 flex-1 overflow-auto">
          {/* Pays de vente */}
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] text-[#666]">
              Pays de vente :{" "}
              <select
                value={saleCountry}
                onChange={(e) => setSaleCountry(e.target.value)}
                className="ml-1 border border-[#E8E8E8] rounded px-1 py-0.5 text-[11px] bg-white"
              >
                <option value="FR">France</option>
                {/* plus tard : autres pays */}
              </select>
            </p>
            <p className="text-[11px] text-[#999]">
              Règles : {rules.label}
            </p>
          </div>

          {/* Statut complétude */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {isComplete ? (
                <CheckCircle
                  className="text-emerald-600"
                  size={16}
                />
              ) : (
                <AlertCircle
                  className="text-amber-600"
                  size={16}
                />
              )}
              <span className="text-sm font-medium text-[#1A1A1A]">
                {isComplete
                  ? "Complet"
                  : `${missingCount} manquante${
                      missingCount > 1 ? "s" : ""
                    }`}
              </span>
            </div>
            <span className="text-sm text-[#666]">
              {totalCount - missingCount}/{totalCount}
            </span>
          </div>
          <div className="w-full h-1 bg-[#E8E8E8] rounded-full overflow-hidden mb-4">
            <div
              className="h-full bg-emerald-600 transition-all"
              style={{
                width: `${
                  totalCount > 0
                    ? ((totalCount - missingCount) / totalCount) * 100
                    : 0
                }%`,
              }}
            />
          </div>

          {/* Badges champs */}
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(completeness).map(([key, info]) => {
              const isLegalRequired =
                rules.legallyRequired?.includes(key);

              return (
                <div
                  key={key}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border ${
                    info.status
                      ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                      : "bg-amber-50 border-amber-200 text-amber-800"
                  }`}
                  title={
                    info.status
                      ? key === "images"
                        ? `${
                            Array.isArray(info.data)
                              ? info.data.length
                              : 0
                          } visuel${
                            Array.isArray(info.data) &&
                            info.data.length > 1
                              ? "s"
                              : ""
                          }`
                        : key === "valuation"
                        ? formatPriceForPDF(info.data)
                        : String(info.data || "")
                      : isLegalRequired
                      ? "Obligatoire pour la vente dans ce pays"
                      : "Information recommandée"
                  }
                >
                  {info.status ? (
                    <CheckCircle
                      size={12}
                      className="flex-shrink-0"
                    />
                  ) : (
                    <AlertCircle
                      size={12}
                      className="flex-shrink-0"
                    />
                  )}
                  <span className="font-medium">
                    {info.label}
                    {isLegalRequired ? " *" : ""}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Contact pour la vente : Expert / Avocat */}
          <div className="border-t border-[#E8E8E8] pt-4 mb-2">
            <p className="text-xs font-medium text-[#666] mb-2">
              PERSONNE RESPONSABLE DE LA MISE EN VENTE
            </p>

            <div className="flex gap-2 mb-3">
              <button
                onClick={() => setContactMode("expert")}
                className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  contactMode === "expert"
                    ? "bg-[#1A1A1A] text-white"
                    : "bg-white text-[#666] border border-[#E8E8E8] hover:border-[#1A1A1A]"
                }`}
              >
                Expert
              </button>
              <button
                onClick={() => setContactMode("lawyer")}
                className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  contactMode === "lawyer"
                    ? "bg-[#1A1A1A] text-white"
                    : "bg-white text-[#666] border border-[#E8E8E8] hover:border-[#1A1A1A]"
                }`}
              >
                Avocat
              </button>
            </div>

            {contactMode === "expert" && (
              <div className="p-3 bg-[#FAFAFA] rounded-lg border border-[#E8E8E8] space-y-2">
                <label className="text-xs text-[#666]">
                  Sélectionner l’expert qui prendra en charge la vente
                </label>
                <select
                  value={selectedExpertId}
                  onChange={(e) =>
                    setSelectedExpertId(e.target.value || "")
                  }
                  className="w-full border border-[#E8E8E8] rounded px-2 py-1.5 text-sm focus:outline-none focus:border-[#1A1A1A]"
                >
                  <option value="">
                    — Aucun expert sélectionné —
                  </option>
                  {experts.map((exp) => {
                    const fullName = `${exp.first_name || ""} ${
                      exp.last_name || ""
                    }`.trim();
                    return (
                      <option key={exp.id} value={exp.id}>
                        {fullName || "Expert sans nom"}{" "}
                        {exp.company ? `(${exp.company})` : ""}
                      </option>
                    );
                  })}
                </select>

                {selectedExpert && (
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-[#666]">
                      Email :{" "}
                      <span className="font-medium">
                        {selectedExpert.email || "Non renseigné"}
                      </span>
                    </p>
                    {selectedExpert.email && (
                      <button
                        onClick={contactExpert}
                        className="inline-flex items-center gap-1 text-xs text-[#1A1A1A] underline hover:no-underline"
                      >
                        <Mail size={12} />
                        Contacter
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {contactMode === "lawyer" && (
              <div className="grid grid-cols-2 gap-2 p-3 bg-[#FAFAFA] rounded-lg border border-[#E8E8E8]">
                <input
                  type="text"
                  placeholder="Nom"
                  value={lawyerInfo.name}
                  onChange={(e) =>
                    setLawyerInfo({
                      ...lawyerInfo,
                      name: e.target.value,
                    })
                  }
                  className="border border-[#E8E8E8] rounded px-2 py-1.5 text-sm col-span-2"
                />
                <input
                  type="text"
                  placeholder="Cabinet"
                  value={lawyerInfo.firm}
                  onChange={(e) =>
                    setLawyerInfo({
                      ...lawyerInfo,
                      firm: e.target.value,
                    })
                  }
                  className="border border-[#E8E8E8] rounded px-2 py-1.5 text-sm col-span-2"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={lawyerInfo.email}
                  onChange={(e) =>
                    setLawyerInfo({
                      ...lawyerInfo,
                      email: e.target.value,
                    })
                  }
                  className="border border-[#E8E8E8] rounded px-2 py-1.5 text-sm col-span-1"
                />
                <input
                  type="text"
                  placeholder="Téléphone"
                  value={lawyerInfo.phone}
                  onChange={(e) =>
                    setLawyerInfo({
                      ...lawyerInfo,
                      phone: e.target.value,
                    })
                  }
                  className="border border-[#E8E8E8] rounded px-2 py-1.5 text-sm col-span-1"
                />
                <p className="col-span-2 text-[11px] text-[#999] mt-1">
                  Nom et email sont obligatoires pour générer le dossier
                  si vous choisissez un avocat.
                </p>
              </div>
            )}
          </div>

          {/* Attestations FO / propriétaire */}
          <div className="border-t border-[#E8E8E8] pt-4 mb-2 space-y-2">
            {needsAuthAttestation && (
              <label className="flex items-start gap-2 text-[11px] text-[#444] bg-[#FDFBF4] border border-amber-200 rounded-md px-3 py-2">
                <input
                  type="checkbox"
                  checked={userCheckedAuthenticity}
                  onChange={(e) =>
                    setUserCheckedAuthenticity(e.target.checked)
                  }
                  className="mt-[2px]"
                />
                <span>
                  Le propriétaire / Family Office{" "}
                  <span className="font-semibold">
                    garantit l’authenticité
                  </span>{" "}
                  de l’œuvre, en l’absence de certificat
                  d’authenticité, conformément à son obligation de
                  délivrance (art. 1603 du Code civil).
                </span>
              </label>
            )}

            {needsPropertyAttestation && (
              <label className="flex items-start gap-2 text-[11px] text-[#444] bg-[#F5FAFF] border border-sky-200 rounded-md px-3 py-2">
                <input
                  type="checkbox"
                  checked={userCheckedProperty}
                  onChange={(e) =>
                    setUserCheckedProperty(e.target.checked)
                  }
                  className="mt-[2px]"
                />
                <span>
                  Le propriétaire / Family Office{" "}
                  <span className="font-semibold">
                    atteste être le détenteur légitime
                  </span>{" "}
                  de l’œuvre, qu’elle n’est pas issue d’un vol ou d’un
                  trafic illicite, conformément à l’article 2279 du
                  Code civil et, le cas échéant, à la Convention UNESCO
                  de 1970.
                </span>
              </label>
            )}
          </div>

          {/* Message de blocage si non autorisé */}
          {!isSaleAuthorized && (
            <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              ⚠️ Pour générer le dossier pour la vente en{" "}
              {rules.label}, il faut :
              <br />• que tous les champs obligatoires pour ce pays
              soient complétés (
              {legalRequiredKeys
                .map((key) => completeness[key]?.label)
                .filter(Boolean)
                .join(", ") || "OK"}
              )
              <br />• désigner un{" "}
              <span className="font-semibold">expert</span> ou un{" "}
              <span className="font-semibold">avocat</span> (nom +
              email)
              {needsAuthAttestation
                ? " • cocher la garantie d’authenticité"
                : ""}
              {needsPropertyAttestation
                ? " • cocher l’attestation de propriété"
                : ""}
              .
            </p>
          )}
        </div>

        {/* Footer actions */}
        <div className="border-t border-[#E8E8E8] px-6 py-3 flex items-center justify-between flex-shrink-0 bg-[#FAFAFA]">
          <button
            onClick={() => setShowNDA(true)}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg border border-[#E8E8E8] hover:border-[#1A1A1A] transition-colors"
          >
            <FileText size={14} />
            Voir / éditer le NDA
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs rounded-lg border border-[#E8E8E8] text-[#333] hover:border-[#1A1A1A] transition-colors"
            >
              Fermer
            </button>
            <button
              onClick={generateSaleDossier}
              disabled={!isSaleAuthorized}
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                isSaleAuthorized
                  ? "bg-[#1A1A1A] text-white hover:bg-[#333]"
                  : "bg-[#E8E8E8] text-[#999] cursor-not-allowed"
              }`}
            >
              <Download size={14} />
              Télécharger le dossier (.zip)
            </button>
          </div>
        </div>

        {/* Modal NDA plein écran dans la carte */}
        {showNDA && (
          <div className="absolute inset-0 bg-white rounded-xl p-6 overflow-auto z-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-serif text-[#1A1A1A]">
                Accord de confidentialité (NDA)
              </h3>
              <button
                onClick={() => setShowNDA(false)}
                className="text-[#666] hover:text-[#1A1A1A]"
              >
                <X size={20} />
              </button>
            </div>
            <textarea
              value={ndaText}
              onChange={(e) => setNdaText(e.target.value)}
              rows={20}
              className="w-full min-h-[500px] border border-[#E8E8E8] rounded-lg p-4 text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-[#1A1A1A] leading-relaxed"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowNDA(false)}
                className="px-3 py-1.5 text-xs rounded-lg border border-[#E8E8E8] hover:border-[#1A1A1A]"
              >
                Fermer
              </button>
              <button
                onClick={() => {
                  setShowNDA(false);
                  alert(
                    "NDA mis à jour pour ce dossier (la version texte sera incluse dans le ZIP)."
                  );
                }}
                className="px-3 py-1.5 text-xs rounded-lg bg-[#1A1A1A] text-white hover:bg-[#333]"
              >
                Sauvegarder dans le ZIP
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
